import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getDeck, getCards, updateCard, updateDeckStats, getUserStarredCards, toggleCardStar, updateUserProgress } from '../services/deckService';
import { Deck, Card } from '../services/deckService';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  LinearProgress,
  Chip,
  Tooltip,
  Container,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';

const StudyPage: React.FC = () => {
  const { t } = useTranslation();
  const { deckId } = useParams<{ deckId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deck, setDeck] = useState<Deck | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [initialVisibleSides, setInitialVisibleSides] = useState<number[]>([0]);
  const [revealableSides, setRevealableSides] = useState<number[]>([1]);
  const [currentVisibleSides, setCurrentVisibleSides] = useState<number[]>([0]);
  const [error, setError] = useState<string | null>(null);
  const [studyProgress, setStudyProgress] = useState({
    total: 0,
    learned: 0,
  });
  const [filteredCards, setFilteredCards] = useState<Card[]>([]);
  const [currentCard, setCurrentCard] = useState<Card | null>(null);
  const [isSessionComplete, setIsSessionComplete] = useState(false);
  const [sessionStats, setSessionStats] = useState({
    totalCards: 0,
    cardsReviewed: 0,
    hardResponses: 0,
    easyResponses: 0,
    learnedCards: 0
  });
  const [isOwner, setIsOwner] = useState(false);
  const [starredCards, setStarredCards] = useState<string[]>([]);
  const [lastPressedKey, setLastPressedKey] = useState<'hard' | 'easy' | 'learned' | null>(null);

  useEffect(() => {
    const fetchDeckAndCards = async () => {
      if (!deckId || !user) return;

      try {
        const deckData = await getDeck(deckId);
        if (!deckData) {
          setError(t('study.errors.deckNotFound'));
          return;
        }

        // All users who can access the deck can study it
        // No need to check permissions here as Firestore rules will handle it
        
        setDeck(deckData);
        // Check if the current user is the owner of the deck
        setIsOwner(deckData.userId === user.uid);
        
        const cardsData = await getCards(deckId);
        
        // Fetch user's starred cards
        const userStarredCards = await getUserStarredCards(user.uid, deckId);
        setStarredCards(userStarredCards);
        
        setCards(cardsData as Card[]);
        
        // Calculate initial progress
        const learned = cardsData.filter(card => card.isLearned).length;
        setStudyProgress({
          total: cardsData.length,
          learned,
        });
        setCurrentCard(cardsData[0]);

        // Initialize userProgress document if needed
        updateUserProgress(user.uid, deckId, learned, cardsData.length)
          .catch(err => console.error(t('study.errors.progressUpdate'), err));
          
      } catch (err) {
        setError(t('study.errors.failedToLoad'));
        console.error(t('study.errors.loadingError'), err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeckAndCards();
  }, [deckId, user, t]);

  useEffect(() => {
    const newFilteredCards = cards.filter(card => 
      !card.isLearned && 
      (card.nextReview === null || (card.nextReview && new Date(card.nextReview) <= new Date()))
    );
    setFilteredCards(newFilteredCards);
    // Если текущая карточка не входит в отфильтрованный список, переходим к первой
    if (!newFilteredCards.some(card => card.id === currentCard?.id)) {
      setCurrentCardIndex(0);
      setCurrentCard(newFilteredCards[0] || null);
    }
  }, [cards, currentCard]);

  useEffect(() => {
    if (filteredCards.length > 0) {
      const allCardsReviewed = filteredCards.every(card => 
        card.nextReview && new Date(card.nextReview) > new Date()
      );
      
      if (allCardsReviewed) {
        setIsSessionComplete(true);
      }
    }
  }, [filteredCards]);

  // Инициализируем статистику при первой загрузке карточек
  useEffect(() => {
    if (cards.length > 0 && sessionStats.totalCards === 0) {
      const initialFilteredCards = cards.filter(card => 
        !card.isLearned && 
        (card.nextReview === null || (card.nextReview && new Date(card.nextReview) <= new Date()))
      );
      console.log('Initializing session stats:', {
        totalCards: initialFilteredCards.length,
        cardsReviewed: 0,
        hardResponses: 0,
        easyResponses: 0,
        learnedCards: 0
      });
      setSessionStats(prev => ({
        ...prev,
        totalCards: initialFilteredCards.length,
        cardsReviewed: 0,
        hardResponses: 0,
        easyResponses: 0,
        learnedCards: 0
      }));
    }
  }, [cards, sessionStats.totalCards]);

  const handleResponse = useCallback(async (response: 'hard' | 'easy' | 'learned') => {
    if (!deckId || !currentCard || !user) return;

    try {
      let updatedCard = { ...currentCard };
      const now = new Date().toISOString();
      
      // Update session statistics
      setSessionStats(prev => {
        const newStats = {
          ...prev,
          cardsReviewed: response !== 'hard' ? prev.cardsReviewed + 1 : prev.cardsReviewed,
          hardResponses: response === 'hard' ? prev.hardResponses + 1 : prev.hardResponses,
          easyResponses: response === 'easy' ? prev.easyResponses + 1 : prev.easyResponses,
          learnedCards: response === 'learned' ? prev.learnedCards + 1 : prev.learnedCards
        };
        console.log('Updating session stats:', newStats);
        return newStats;
      });
      
      switch (response) {
        case 'hard':
          // Card remains in the current session
          updatedCard = {
            ...updatedCard,
            lastShown: now,
            nextReview: null,
            easyStreak: 0 // Reset easy answers counter
          };
          break;
        case 'easy':
          // Increase easy answers counter
          const newEasyStreak = (updatedCard.easyStreak || 0) + 1;
          const isLearned = newEasyStreak >= 3;
          
          updatedCard = {
            ...updatedCard,
            lastShown: now,
            nextReview: new Date(Date.now() + 1000 * 60 * 60 * 12).toISOString(), // 12 hours
            easyStreak: newEasyStreak,
            isLearned: isLearned
          };

          // If the card became learned after three easy answers
          if (isLearned) {
            setSessionStats(prev => ({
              ...prev,
              learnedCards: prev.learnedCards + 1
            }));
          }
          break;
        case 'learned':
          updatedCard = {
            ...updatedCard,
            isLearned: true,
            lastShown: now,
            easyStreak: 0
          };
          break;
      }

      // Only update Firestore if the user is the owner of the deck
      if (isOwner) {
        await updateCard(deckId, currentCard.id, currentCard.sides.map(side => side.content), updatedCard);
        // Update deck stats only if the user is the owner
        await updateDeckStats(deckId);
      }
      
      // Always update local state
      setCards(prevCards => {
        const newCards = prevCards.map(card => 
          card.id === currentCard.id ? updatedCard : card
        );
        
        // Calculate cards learned
        const cardsLearned = newCards.filter(card => card.isLearned).length;
        
        // Update user progress in Firestore
        updateUserProgress(user.uid, deckId, cardsLearned, newCards.length)
          .catch(err => console.error('Error updating user progress:', err));
        
        return newCards;
      });
      
      // Переходим к следующей карточке
      const nextIndex = currentCardIndex < filteredCards.length - 1 ? currentCardIndex + 1 : 0;
      setCurrentCardIndex(nextIndex);
      setCurrentCard(filteredCards[nextIndex]);
    } catch (error) {
      console.error('Error updating card:', error);
      
      // Even if the Firestore update fails, still update local state for non-owners
      if (!isOwner) {
        const updatedCard = { ...currentCard };
        const now = new Date().toISOString();
        
        switch (response) {
          case 'hard':
            updatedCard.lastShown = now;
            updatedCard.nextReview = null;
            updatedCard.easyStreak = 0;
            break;
          case 'easy':
            const newEasyStreak = (updatedCard.easyStreak || 0) + 1;
            const isLearned = newEasyStreak >= 3;
            
            updatedCard.lastShown = now;
            updatedCard.nextReview = new Date(Date.now() + 1000 * 60 * 2).toISOString();
            updatedCard.easyStreak = newEasyStreak;
            updatedCard.isLearned = isLearned;
            break;
          case 'learned':
            updatedCard.isLearned = true;
            updatedCard.lastShown = now;
            updatedCard.easyStreak = 0;
            break;
        }
        
        setCards(prevCards => {
          const newCards = prevCards.map(card => 
            card.id === currentCard.id ? updatedCard : card
          );
          return newCards;
        });
        
        const nextIndex = currentCardIndex < filteredCards.length - 1 ? currentCardIndex + 1 : 0;
        setCurrentCardIndex(nextIndex);
        setCurrentCard(filteredCards[nextIndex]);
      }
    }
  }, [deckId, filteredCards, currentCardIndex, currentCard, setSessionStats, setCards, setCurrentCardIndex, setCurrentCard, isOwner, user]);

  // Функция для снятия фокуса с активного элемента
  const removeFocus = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  // Функция для показа сторон для раскрытия (при нажатии)
  const handleCardPress = useCallback(() => {
    // Показываем только стороны для раскрытия, без начальных
    setCurrentVisibleSides([...revealableSides].sort());
  }, [revealableSides]);
  
  // Функция для возврата к начальным сторонам (при отпускании)
  const handleCardRelease = useCallback(() => {
    // Возвращаемся к исходным сторонам
    setCurrentVisibleSides([...initialVisibleSides]);
  }, [initialVisibleSides]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Пропускаем нажатия, если включен input или textarea
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }
      
      // Пропускаем, если зажаты модификаторы
      if (event.ctrlKey || event.altKey || event.metaKey) {
        return;
      }
      
      if (event.key === '1') {
        setLastPressedKey('hard');
        handleResponse('hard');
        setTimeout(() => setLastPressedKey(null), 300);
      } else if (event.key === '2') {
        setLastPressedKey('easy');
        handleResponse('easy');
        setTimeout(() => setLastPressedKey(null), 300);
      } else if (event.key === 'Enter') {
        // Клавиша Enter для "Изучено"
        setLastPressedKey('learned');
        handleResponse('learned');
        setTimeout(() => setLastPressedKey(null), 300);
      } else if (event.key === ' ' && !event.repeat) {
        // Предотвращаем стандартное поведение пробела (прокрутка страницы)
        event.preventDefault();
        // Пробел для переворота карточки (показа скрытых сторон)
        handleCardPress();
      }
    };
    
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === ' ') {
        // При отпускании пробела, возвращаемся к исходным сторонам
        handleCardRelease();
      }
    };

    // Keyboard event handlers
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleResponse, handleCardPress, handleCardRelease]);

  const handleStarCard = useCallback(async () => {
    if (!deckId || !currentCard || !user) return;

    try {
      // Toggle star in Firestore user progress
      await toggleCardStar(user.uid, deckId, currentCard.id);
      
      // Update local state for starred cards
      setStarredCards(prev => {
        const isCurrentlyStarred = prev.includes(currentCard.id);
        if (isCurrentlyStarred) {
          return prev.filter(id => id !== currentCard.id);
        } else {
          return [...prev, currentCard.id];
        }
      });
    } catch (err) {
      console.error('Error toggling card star:', err);
    }
  }, [deckId, currentCard, setStarredCards, user]);

  const handleToggleInitialSide = (index: number) => {
    setInitialVisibleSides(prev => {
      // Если сторона уже выбрана, удаляем ее
      if (prev.includes(index)) {
        // Не позволяем убрать последнюю видимую сторону
        if (prev.length === 1) return prev;
        return prev.filter(sideIndex => sideIndex !== index);
      } 
      // Иначе добавляем ее
      return [...prev, index].sort();
    });
    
    // Обновляем текущие видимые стороны, если мы изменяем настройки
    setCurrentVisibleSides(prev => {
      // Если сторона уже видима и ее убирают из initialVisibleSides, оставляем как есть
      // (предполагаем, что пользователь уже раскрыл карточку)
      
      // Если сторона добавляется в initialVisibleSides и ее еще нет в visible, добавляем
      if (!prev.includes(index) && !initialVisibleSides.includes(index)) {
        return [...prev, index].sort();
      }
      return prev;
    });
  };
  
  // Обработчик для переключения сторон, открывающихся при клике
  const handleToggleRevealableSide = (index: number) => {
    setRevealableSides(prev => {
      // Если сторона уже выбрана, удаляем ее
      if (prev.includes(index)) {
        // Проверяем, останется ли хотя бы одна сторона для открытия
        // Если все начальные стороны уже открыты, нужно оставить хотя бы одну для открытия
        const wouldRemainSides = prev.filter(sideIndex => sideIndex !== index);
        if (wouldRemainSides.length === 0 && initialVisibleSides.length === currentCard?.sides.length) {
          return prev; // Не позволяем убрать все стороны
        }
        return wouldRemainSides;
      } 
      // Иначе добавляем ее
      return [...prev, index].sort();
    });
  };
  
  const handleResetSession = async () => {
    if (!deckId || !user) return;
    
    try {
      const updatedCards = [...cards];
      
      // Only update Firestore if user is owner
      if (isOwner) {
        // Сбрасываем nextReview для всех карточек
        const resetPromises = cards.map(card => 
          updateCard(deckId, card.id, card.sides.map(side => side.content), {
            ...card,
            nextReview: null,
            easyStreak: 0
          })
        );
        
        await Promise.all(resetPromises);
        // Get updated cards from server
        const serverUpdatedCards = await getCards(deckId);
        setCards(serverUpdatedCards);
        
        // Update user progress
        const cardsLearned = serverUpdatedCards.filter(card => card.isLearned).length;
        await updateUserProgress(user.uid, deckId, cardsLearned, serverUpdatedCards.length);
      } else {
        // Just update local state for non-owners
        updatedCards.forEach(card => {
          card.nextReview = null;
          card.easyStreak = 0;
        });
        setCards(updatedCards);
        
        // Update user progress
        const cardsLearned = updatedCards.filter(card => card.isLearned).length;
        await updateUserProgress(user.uid, deckId, cardsLearned, updatedCards.length);
      }
      
      setCurrentCardIndex(0);
      setCurrentCard(updatedCards[0]);
      setIsSessionComplete(false);
      // Сбрасываем статистику сессии
      setSessionStats({
        totalCards: updatedCards.filter(card => !card.isLearned).length,
        cardsReviewed: 0,
        hardResponses: 0,
        easyResponses: 0,
        learnedCards: 0
      });
    } catch (error) {
      console.error('Error resetting session:', error);
      
      // If Firestore update fails but user is not owner, still update local state
      if (!isOwner) {
        const updatedCards = [...cards];
        updatedCards.forEach(card => {
          card.nextReview = null;
          card.easyStreak = 0;
        });
        
        setCards(updatedCards);
        setCurrentCardIndex(0);
        setCurrentCard(updatedCards[0]);
        setIsSessionComplete(false);
        setSessionStats({
          totalCards: updatedCards.filter(card => !card.isLearned).length,
          cardsReviewed: 0,
          hardResponses: 0,
          easyResponses: 0,
          learnedCards: 0
        });
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleResetStatuses = async () => {
    if (!deckId || !user) return;
    
    try {
      const updatedCards = [...cards];
      
      // Only update Firestore if user is owner
      if (isOwner) {
        // Сбрасываем статусы всех карточек
        const resetPromises = cards.map(card => 
          updateCard(deckId, card.id, card.sides.map(side => side.content), {
            ...card,
            isLearned: false,
            nextReview: null,
            easyStreak: 0
          })
        );
        
        await Promise.all(resetPromises);
        
        // Обновляем локальное состояние
        const serverUpdatedCards = await getCards(deckId);
        setCards(serverUpdatedCards);
        
        // Обновляем статистику колоды
        await updateDeckStats(deckId);
        
        // Update user progress
        await updateUserProgress(user.uid, deckId, 0, serverUpdatedCards.length);
      } else {
        // Just update local state for non-owners
        updatedCards.forEach(card => {
          card.isLearned = false;
          card.nextReview = null;
          card.easyStreak = 0;
        });
        setCards(updatedCards);
        
        // Update user progress
        await updateUserProgress(user.uid, deckId, 0, updatedCards.length);
      }
      
      // Обновляем прогресс изучения
      setStudyProgress({
        total: updatedCards.length,
        learned: 0
      });
      
      // Обновляем статистику сессии
      setSessionStats({
        totalCards: updatedCards.length,
        cardsReviewed: 0,
        hardResponses: 0,
        easyResponses: 0,
        learnedCards: 0
      });
      
      // Сбрасываем сессию
      setCurrentCardIndex(0);
      setCurrentCard(updatedCards[0]);
      setIsSessionComplete(false);
    } catch (error) {
      console.error('Error resetting statuses:', error);
      
      // If Firestore update fails but user is not owner, still update local state
      if (!isOwner) {
        const updatedCards = [...cards];
        updatedCards.forEach(card => {
          card.isLearned = false;
          card.nextReview = null;
          card.easyStreak = 0;
        });
        
        setCards(updatedCards);
        setStudyProgress({
          total: updatedCards.length,
          learned: 0
        });
        setSessionStats({
          totalCards: updatedCards.length,
          cardsReviewed: 0,
          hardResponses: 0,
          easyResponses: 0,
          learnedCards: 0
        });
        setCurrentCardIndex(0);
        setCurrentCard(updatedCards[0]);
        setIsSessionComplete(false);
      }
    }
  };

  // Сбрасываем карточку к начальному состоянию при смене карточки
  useEffect(() => {
    if (currentCard) {
      // Показываем только начальные стороны
      setCurrentVisibleSides([...initialVisibleSides]);
    }
  }, [currentCard, initialVisibleSides]);

  const SessionCompleteScreen = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('study.sessionComplete')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('study.sessionStats', { 
          reviewed: sessionStats.cardsReviewed,
          total: sessionStats.totalCards,
          learned: sessionStats.learnedCards
        })}
      </Typography>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        maxWidth: '350px', 
        mx: 'auto', 
        mb: 4,
        gap: 1 
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography>{t('study.cardsReviewed')}:</Typography>
          <Typography>{sessionStats.cardsReviewed}/{sessionStats.totalCards}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography>{t('study.easyResponses')}:</Typography>
          <Typography>{sessionStats.easyResponses}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography>{t('study.hardResponses')}:</Typography>
          <Typography>{sessionStats.hardResponses}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography>{t('study.cardsLearned')}:</Typography>
          <Typography>{sessionStats.learnedCards}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography>{t('study.overallProgress')}:</Typography>
          <Typography>{studyProgress.learned}/{studyProgress.total}</Typography>
        </Box>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        justifyContent: 'center',
        flexWrap: 'wrap' 
      }}>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleResetSession}
          startIcon={<RefreshIcon />}
        >
          {t('study.newSession')}
        </Button>
        <Button 
          variant="outlined"
          onClick={() => navigate(`/decks/${deckId}`)}
          startIcon={<ArrowBackIcon />}
        >
          {t('common.backToDeck')}
        </Button>
      </Box>
    </Box>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>{t('common.loading')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
        <Button onClick={() => navigate('/decks')} startIcon={<ArrowBackIcon />}>
          {t('common.backToDecks')}
        </Button>
      </Box>
    );
  }

  if (!deck || cards.length === 0) {
    return (
      <Box p={3}>
        <Typography>{t('study.noCards')}</Typography>
        <Button onClick={() => navigate(`/decks/${deckId}`)} startIcon={<ArrowBackIcon />}>
          {t('common.backToDeck')}
        </Button>
      </Box>
    );
  }

  // Если все карточки изучены, показываем сообщение о завершении
  if (studyProgress.learned === studyProgress.total) {
    return (
      <Box p={3} sx={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
        <Box mb={4}>
          <Typography variant="h3" gutterBottom>
            {deck?.title}
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {deck?.description}
          </Typography>
        </Box>
        
        <Paper elevation={3} sx={{ p: 4, mb: 3, bgcolor: 'background.paper' }}>
          <Typography variant="h4" color="success.main" gutterBottom>
            {t('study.congratulations')} 🎉
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('study.allCardsLearned')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('study.allCardsLearnedCount', { count: studyProgress.total })}
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate(`/decks/${deckId}`)}
            sx={{ mt: 2 }}
          >
            {t('common.backToDeck')}
          </Button>
        </Paper>
      </Box>
    );
  }

  // Если все карточки в сессии отложены на повторение, показываем экран завершения сессии
  if (filteredCards.every(card => card.nextReview && new Date(card.nextReview) > new Date())) {
    return <SessionCompleteScreen />;
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 3 }}>
        {isSessionComplete ? (
          <SessionCompleteScreen />
        ) : (
          <>
            {/* Header */}
            <Box sx={{ 
              display: 'flex', 
              mb: { xs: 2, sm: 3 },
              flexDirection: 'column',
              gap: { xs: 1, sm: 1 },
              alignItems: { xs: 'flex-start', sm: 'flex-start' },
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                width: '100%',
                justifyContent: 'space-between'
              }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(`/decks/${deckId}`)}
                >
                  {t('common.back')}
                </Button>
                
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  width: { xs: 'auto', sm: 'auto' },
                  justifyContent: 'flex-end'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    {sessionStats.cardsReviewed}/{sessionStats.totalCards}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(sessionStats.cardsReviewed / sessionStats.totalCards) * 100}
                    sx={{ 
                      width: { xs: 100, sm: 200 },
                      height: 8,
                      borderRadius: 4
                    }}
                  />
                </Box>
              </Box>
              
              <Typography 
                variant="h5" 
                sx={{ 
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  fontWeight: 'bold'
                }}
              >
                {deck?.title}
              </Typography>
            </Box>

            {/* Card */}
            {currentCard && (
              <Paper 
                elevation={3}
                sx={{
                  p: { xs: 2, sm: 4 },
                  mb: { xs: 2, sm: 3 },
                  minHeight: { xs: '30vh', sm: '40vh' },
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  display: 'flex',
                  gap: 1
                }}>
                  <IconButton
                    onClick={() => handleStarCard()}
                    size="small"
                    color={starredCards.includes(currentCard.id) ? 'warning' : 'default'}
                  >
                    {starredCards.includes(currentCard.id) ? <StarIcon color="warning" /> : <StarBorderIcon />}
                  </IconButton>
                </Box>

                <Box 
                  sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexGrow: 1,
                    gap: 3,
                    width: '100%',
                    cursor: 'pointer',
                    userSelect: 'none', // Предотвращаем выделение текста при нажатии
                    transition: 'background-color 0.2s'
                  }}
                  onMouseDown={handleCardPress}
                  onMouseUp={handleCardRelease}
                  onMouseLeave={handleCardRelease}
                  onTouchStart={handleCardPress}
                  onTouchEnd={handleCardRelease}
                  onTouchCancel={handleCardRelease}
                >
                  {currentVisibleSides.map(sideIndex => (
                    <Box key={sideIndex} sx={{ width: '100%', textAlign: 'center' }}>
                      <Typography 
                        variant="h4" 
                        component="div" 
                        align="center"
                        sx={{ 
                          fontSize: { xs: '1.5rem', sm: '2.125rem' },
                          wordBreak: 'break-word'
                        }}
                      >
                        {currentCard.sides[sideIndex].content}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  mt: 2,
                  width: '100%'
                }}>
                  <Box>
                    <Box sx={{ 
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 1,
                      flexWrap: 'wrap'
                    }}>
                      {currentCard.sides.map((side, index) => (
                        <Tooltip 
                          key={`initial-${index}`} 
                          title={deck?.sideNames[index] || `${t('study.side')} ${index + 1}`}
                          placement="top"
                          arrow
                        >
                          <Chip
                            label={deck?.sideNames[index] || `${index + 1}`}
                            onClick={() => {
                              handleToggleInitialSide(index);
                              removeFocus(); // Снимаем фокус после клика
                            }}
                            onKeyDown={(e) => {
                              // Предотвращаем действие пробела в любом случае
                              if (e.key === ' ') {
                                e.stopPropagation();
                              }
                            }}
                            color={initialVisibleSides.includes(index) ? 'primary' : 'default'}
                            variant={initialVisibleSides.includes(index) ? 'filled' : 'outlined'}
                            sx={{ minWidth: 80 }}
                            tabIndex={-1} // Делаем элемент не фокусируемым с клавиатуры
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                  
                  <Box>
                    <Box sx={{ 
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 1,
                      flexWrap: 'wrap'
                    }}>
                      {currentCard.sides.map((side, index) => (
                        <Tooltip 
                          key={`reveal-${index}`} 
                          title={deck?.sideNames[index] || `${t('study.side')} ${index + 1}`}
                          placement="top"
                          arrow
                        >
                          <Chip
                            label={deck?.sideNames[index] || `${index + 1}`}
                            onClick={() => {
                              handleToggleRevealableSide(index);
                              removeFocus(); // Снимаем фокус после клика
                            }}
                            onKeyDown={(e) => {
                              // Предотвращаем действие пробела в любом случае
                              if (e.key === ' ') {
                                e.stopPropagation();
                              }
                            }}
                            color={revealableSides.includes(index) ? 'success' : 'default'}
                            variant={revealableSides.includes(index) ? 'filled' : 'outlined'}
                            sx={{ minWidth: 80 }}
                            tabIndex={-1} // Делаем элемент не фокусируемым с клавиатуры
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Controls */}
            <Box sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 2 },
              justifyContent: 'center',
              alignItems: 'stretch'
            }}>
              <Box sx={{ 
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'row' },
                gap: { xs: 1, sm: 2 },
                width: { xs: '100%', sm: 'auto' }
              }}>
                <Button
                  variant={lastPressedKey === 'hard' ? "contained" : "outlined"}
                  color="error"
                  fullWidth
                  onClick={() => {
                    setLastPressedKey('hard');
                    handleResponse('hard');
                    setTimeout(() => setLastPressedKey(null), 300);
                  }}
                  sx={{ 
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    position: 'relative'
                  }}
                >
                  {t('study.markAsHard')}
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 2, 
                    right: 2, 
                    bgcolor: 'error.dark',
                    color: 'white',
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    fontSize: '0.6rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}>1</Box>
                </Button>
                <Button
                  variant={lastPressedKey === 'easy' ? "contained" : "outlined"}
                  color="warning"
                  fullWidth
                  onClick={() => {
                    setLastPressedKey('easy');
                    handleResponse('easy');
                    setTimeout(() => setLastPressedKey(null), 300);
                  }}
                  sx={{ 
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    position: 'relative'
                  }}
                >
                  {t('study.markAsEasy')}
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 2, 
                    right: 2, 
                    bgcolor: 'warning.dark',
                    color: 'white',
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    fontSize: '0.6rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}>2</Box>
                </Button>
              </Box>
              <Button
                variant={lastPressedKey === 'learned' ? "contained" : "outlined"}
                color="success"
                fullWidth
                onClick={() => {
                  setLastPressedKey('learned');
                  handleResponse('learned');
                  setTimeout(() => setLastPressedKey(null), 300);
                }}
                sx={{ 
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  width: { xs: '100%', sm: 'auto' },
                  position: 'relative'
                }}
              >
                {t('study.markAsLearned')}
                <Box sx={{ 
                  position: 'absolute', 
                  top: 2, 
                  right: 2, 
                  bgcolor: 'success.dark',
                  color: 'white',
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  fontSize: '0.6rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}>↵</Box>
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
};

export default StudyPage; 